import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckboxElement,
  SelectElement,
  TextFieldElement,
  TextareaAutosizeElement,
  useForm,
} from "react-hook-form-mui";
import { z } from "zod";

import { zodResolver } from "@hookform/resolvers/zod";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation, Storage } from "aws-amplify";
import {
  agentDocumentsByAgentId,
  getLLMAgent,
  openAIAgentDocumentsByAgentId,
} from "../graphql/queries";
import { createLLMAgentMCP, updateLLMAgentMCP } from "../graphql/mutations";
import {
  AgentDocumentsByAgentIdQuery,
  GetLLMAgentDocumentQuery,
  GetLLMAgentQuery,
  OpenAIAgentDocumentsByAgentIdQuery,
  LLMEmailCaptureMode,
  LLMThinkingDepth,
  LLMVisibility,
} from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { useUser } from "../contexts/useUser";
import { updateLLMAgentAfterEdit } from "../dataaccess/updateLLMAgentAfterEdit";
import { createLLMAgentAfterEdit } from "../dataaccess/createLLMAgentAfterEdit";
import { ValidationTab } from "./ValidationTab";
import { AgentVersionTabs } from "./AgentVersionTabs";
import { ChatPanel } from "llmasaservice-ui";
import {
  materialDark,
  materialLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { fetchLLMGroups } from "../dataaccess/fetchLLMGroups";
import { fetchLLMAgents } from "../dataaccess/fetchLLMAgents";
import { parseAgentId, formatVersionForDisplay } from "../utils/agentVersioning";
import { apiKeyManagementHeaders } from "../utils/apiKeyManagementAuth";
import { fetchLLMModels } from "../dataaccess/fetchLLMModels";
import FileUploader from "./FileUploader";
import UrlScrape from "./UrlScrape";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { isProduction } from "../enterprise/tokenmanagement";

const initialValues = {
  name: "",
  status: "active",
  description: "",
  systemInstructions: "",
  systemInstructionsAction: "append",
  groupId: "",

  displayTheme: "dark",
  cssUrl: "",
  displayTitle: "Your agent",
  displayStartMessageOrPrompt: "message",
  displayInitialMessageOrPrompt: "Enter your prompt here",
  displayPromptTemplate: "{{prompt}}",
  displayFollowOnPrompts: "summarize",
  displayActions: "[]",
  displayWidth: "100%",
  displayHeight: "100vh",
  data: "",
  displayPlaceholder: "Type a message",
  displayHideInitialPrompt: true,
  displayShowSaveButton: true,
  displayShowEmailButton: true,
  displayShowCallToAction: false,
  displayCallToActionButtonText: "Submit",
  displayCallToActionEmailAddress: "",
  displayCallToActionEmailSubject: "Agent CTA submitted",
  displayScrollToEnd: false,
  ragQueryLimit: 10,
  ragRankLimit: 5,
  ragIncludeCitations: false,
  ragSkipChecksOnContext: true,
  ragShadowRead: false,

  visibility: LLMVisibility.ALL,
  createConversationOnFirstChat: true,
  customerEmailCaptureMode: LLMEmailCaptureMode.HIDE,
  customerEmailCapturePlaceholder: "Please enter your email...",

  injectMemories: 0,
  injectKnowledgeGraph: 0,
  enableWebSearch: false,
  linkedFileTypes: "",
  thinkingDepth: LLMThinkingDepth.OFF,
  enableRAG: false,
  enableMCP: false,

  enablePromptCaching: true,
  compactionPolicy: "OFF",
  compactionThreshold: 150000,
  curateToolResults: false,
  curateAfterTurns: 4,
};

const schema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, "Name is required"),
    status: z.string().min(1, "Status is required"),
    description: z.string().optional().nullable(),

    systemInstructions: z.string().optional().nullable(),
    systemInstructionsAction: z.string().optional().nullable(),
    groupId: z.string().optional().nullable(),

    displayTheme: z.string().optional().nullable(),
    cssUrl: z.string().optional().nullable(),
    displayTitle: z.string().optional().nullable(),
    displayStartMessageOrPrompt: z.string().optional().nullable(),
    displayInitialMessageOrPrompt: z.string().optional().nullable(),
    displayPromptTemplate: z.string().optional().nullable(),
    displayFollowOnPrompts: z.string().optional().nullable(),
    displayActions: z.string().optional().nullable(),
    displayWidth: z.string().optional().nullable(),
    displayHeight: z.string().optional().nullable(),
    data: z.string().optional().nullable(),
    displayPlaceholder: z.string().optional().nullable(),
    displayHideInitialPrompt: z.boolean().optional().nullable(),
    displayShowSaveButton: z.boolean().optional().nullable(),
    displayShowEmailButton: z.boolean().optional().nullable(),
    displayShowCallToAction: z.boolean().optional().nullable(),
    displayCallToActionButtonText: z.string().optional().nullable(),
    displayCallToActionEmailAddress: z.string().optional().nullable(),
    displayCallToActionEmailSubject: z.string().optional().nullable(),
    displayScrollToEnd: z.boolean().optional().nullable(),
    visibility: z.any().optional().nullable(),
    createConversationOnFirstChat: z.boolean().optional().nullable(),
    customerEmailCaptureMode: z.any().optional().nullable(),
    customerEmailCapturePlaceholder: z.string().optional().nullable(),
    ragIncludeCitations: z.boolean().optional().nullable(),
    ragSkipChecksOnContext: z.boolean().optional().nullable(),
    ragShadowRead: z.boolean().optional().nullable(),
    ragQueryLimit: z
      .number()
      .min(5, "Query limit should be greater or equal to 10. If unsure use 10.")
      .max(50, "Query limit should be less than 50. If unsure use 10.")
      .optional()
      .nullable(),
    ragRankLimit: z
      .number()
      .min(3, "Rerank limit should be greater or equal to 3. If unsure use 5.")
      .max(50, "Rerank limit should be less than 50. If unsure use 5.")
      .optional()
      .nullable(),
    injectMemories: z
      .number()
      .min(0, "Memories to inject should be 0 or greater")
      .max(25, "Memories to inject should be 25 or less")
      .optional()
      .nullable(),
    injectKnowledgeGraph: z
      .number()
      .min(0, "Knowledge Graph to inject should be 0 or greater")
      .max(1, "Knowledge Graph to inject should be 1 or less")
      .optional()
      .nullable(),
    enableWebSearch: z.boolean().optional().nullable(),
    linkedFileTypes: z.string().optional().nullable(),
    thinkingDepth: z.nativeEnum(LLMThinkingDepth).optional().nullable(),
    enableRAG: z.boolean().optional().nullable(),
    enableMCP: z.boolean().optional().nullable(),
    enablePromptCaching: z.boolean().optional().nullable(),
    compactionPolicy: z.string().optional().nullable(),
    compactionThreshold: z
      .number()
      .min(10000, "Compaction threshold must be at least 10,000 tokens")
      .max(1000000, "Compaction threshold must be at most 1,000,000 tokens")
      .optional()
      .nullable(),
    curateToolResults: z.boolean().optional().nullable(),
    curateAfterTurns: z
      .number()
      .min(1, "Must be at least 1 turn")
      .max(50, "Must be at most 50 turns")
      .optional()
      .nullable(),
  })
  .refine(
    (data) =>
      data.ragRankLimit === null ||
      data.ragRankLimit === undefined ||
      data.ragRankLimit <= (data.ragQueryLimit ?? 5),
    {
      message: "Rerank limit must be less than or equal to Query limit",
      path: ["ragRankLimit"],
    }
  );

type AgentFormType = z.infer<typeof schema>;

const AgentForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();

  // Derive version label for UI display
  const versionLabel = id ? formatVersionForDisplay(parseAgentId(id).version) : null;

  const { project, trialDaysRemaining, subscriptionActive } =
    useUser();
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [confirmDelete, setConfirmDelete] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);
  const [staticUrl, setStaticUrl] = React.useState<string | null>(null);
  const [staticHeight, setStaticHeight] = React.useState("600");
  const [styleText, setStyleText] = React.useState("");
  const [apiKey1, setApiKey1] = React.useState("");
  const [mcpUrl, setMcpUrl] = React.useState("");
  const [mcpUrlError, setMcpUrlError] = React.useState(false);
  const [mcpApiKey, setMcpApiKey] = React.useState("");
  const [showMcpApiKey, setShowMcpApiKey] = React.useState(false);
  const [openMCPDialog, setOpenMCPDialog] = React.useState(false);
  const [showDeletedMCPs, setShowDeletedMCPs] = useState(false);
  const [executionMode, setExecutionMode] = React.useState("CLIENT");
  const [unsavedCloseDialogOpen, setUnsavedCloseDialogOpen] = React.useState(false);
  const [saveConflictMessage, setSaveConflictMessage] = React.useState<string | null>(null);
  const showDevRagControls = false;

  const getAPIKeys = async () => {
    console.log(project?.id);

    API.get("eventapi", "/getIntegrationApiKeys", {
      headers: await apiKeyManagementHeaders(),
      queryStringParameters: {
        projectId: project?.id,
      },
    })
      .then((response) => {
        if (response.success) {
          const keys = JSON.parse(response.body);

          console.log(keys);

          if (!keys || (!keys.key1Configured && !keys.key2Configured && !keys.key1 && !keys.key2)) {
            createAPIKeys();
          } else {
            setApiKey1(keys.key1 || "YOUR_INTEGRATION_API_KEY");
            //setApiKey2(keys.key2);
            //setKey1LastRotated(keys.key1LastRotated);
            //setKey2LastRotated(keys.key2LastRotated);
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const createAPIKeys = async () => {
    const response = await API.post("eventapi", "/createIntegrationApiKeys", {
      headers: await apiKeyManagementHeaders(),
      body: {
        projectId: project?.id,
        projectName: project?.name,
      },
    });

    if (response.success) {
      const keys = JSON.parse(response.body);
      setApiKey1(keys.key1);
      //setApiKey2(keys.key2);
      //setKey1LastRotated(keys.key1LastRotated);
      //setKey2LastRotated(keys.key2LastRotated);
    }
  };

  useEffect(() => {
    if (project?.id && (!apiKey1 || apiKey1 === "")) {
      getAPIKeys();
    }
  }, [project?.id]);

  const queryClient = useQueryClient();

  const mutationUpdateAgent = useMutation({
    mutationFn: ({ input, condition }: { input: any; condition?: any }) =>
      updateLLMAgentAfterEdit(input, condition),
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      navigate("/agents");
    },
    onError: (error: any) => {
      // Check for concurrency conflict (DynamoDB conditional check failure)
      const errorMessage = error?.errors?.[0]?.message || error?.message || "";
      if (errorMessage.includes("ConditionalCheckFailedException") || errorMessage.includes("The conditional request failed")) {
        setSaveConflictMessage(
          "This agent was modified by someone else. Immediately copy your changes to the clipboard. Refresh the page to load the latest version, then re-apply your changes."
        );
        logError("Save conflict: This agent was modified by someone else. Please refresh and try again.", error);
      } else {
        logError("Error updating agent", error);
      }
    },
  });

  const mcpCreateMutation = useMutation({
    mutationFn: ({
      mcpUrl,
      executionMode,
      accessToken,
    }: {
      mcpUrl: string;
      executionMode: string;
      accessToken?: string;
    }) =>
      API.graphql(
        graphqlOperation(createLLMAgentMCP, {
          input: {
            agentId: id,
            projectId: project?.id,
            status: "active",
            url: mcpUrl,
            executionMode: executionMode,
            accessToken: accessToken || null,
          },
        })
      ) as Promise<any>,

    onSuccess: () => {
      setOpenMCPDialog(false);
      setMcpUrl("");
      setMcpApiKey("");
      setShowMcpApiKey(false);
      setExecutionMode("CLIENT");
      //queryClient.invalidateQueries({
      //  queryKey: ["agents", { id: id }],
      //});
    },
    onError: (err: any) => {
      logError("Error adding MCP client", err as any);
    },
  });

  const mutationCreateAgent = useMutation({
    mutationFn: createLLMAgentAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      navigate("/agents");
    },
    onError: (error) => {
      logError("Error creating agent", error as any);
    },
  });

  const {
    data: agentData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["agents", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMAgent, { id: id })) as Promise<
        GraphQLResult<GetLLMAgentQuery>
      >,
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: groupData,
    isLoading: isLoadingGroups,
    isError: isErrorGroups,
  } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchLLMGroups(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  // Fetch all agents for version management
  const { data: allAgentsData } = useQuery({
    queryKey: ["agents"],
    queryFn: () => fetchLLMAgents(project?.id ?? ""),
    enabled: !!project?.id,
  });

  const {
    data: modelsData,
    isLoading: isLoadingModels,
    isError: isErrorModels,
  } = useQuery({
    queryKey: ["models"],
    queryFn: () => fetchLLMModels(),
    enabled: true,
  });

  const { data: documentsData, isLoading: isLoadingDocuments } = useQuery({
    queryKey: ["LLMAgentDocuments", { agentId: id }],
    queryFn: () =>
      API.graphql(
        graphqlOperation(agentDocumentsByAgentId, { agentId: id })
      ) as Promise<GraphQLResult<AgentDocumentsByAgentIdQuery>>,
    enabled: !!id,
  });

  const { data: openAIDocumentsData } = useQuery({
    queryKey: ["LLMOpenAIAgentDocuments", { agentId: id }],
    queryFn: () =>
      API.graphql(
        graphqlOperation(openAIAgentDocumentsByAgentId, {
          agentId: id,
          backend: { eq: "openai" },
          filter: { status: { eq: "ready" } },
          limit: 1000,
        })
      ) as Promise<GraphQLResult<OpenAIAgentDocumentsByAgentIdQuery>>,
    enabled: !!id,
  });

  const openAIReadyDocumentIds = new Set(
    openAIDocumentsData?.data?.openAIAgentDocumentsByAgentId?.items
      ?.map((doc) => doc?.documentId)
      .filter(Boolean) ?? []
  );

  const openAIIndexedDocuments =
    documentsData?.data?.agentDocumentsByAgentId?.items?.filter(
      (doc) => doc?.id && openAIReadyDocumentIds.has(doc.id)
    ) ?? [];

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<AgentFormType>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  // Reset form when agent data loads
  useEffect(() => {
    if (agentData?.data?.getLLMAgent) {
      const agentFormData = {
        ...initialValues,
        ...agentData.data.getLLMAgent,
        ragSkipChecksOnContext:
          agentData.data.getLLMAgent.ragSkipChecksOnContext ?? true,
        ragShadowRead:
          (agentData.data.getLLMAgent as any).ragShadowRead ?? false,
        linkedFileTypes:
          (agentData.data.getLLMAgent as any).linkedFileTypes ?? "",
      };
      reset(agentFormData);
    } else if (!id) {
      // Reset to initial values for new agent
      reset(initialValues);
    }
  }, [agentData?.data?.getLLMAgent, id, reset]);

  // Group models by host
  const modelsByHost = React.useMemo(() => {
    if (!modelsData?.data?.listLLMModels?.items) return {};
    
    const grouped: { [host: string]: any[] } = {};
    
    modelsData.data.listLLMModels.items.forEach((model) => {
      const host = model?.host || 'Unknown Host';
      if (!grouped[host]) {
        grouped[host] = [];
      }
      grouped[host].push({
        id: `model:${model?.id}`,
        label: model?.name || 'Unknown Model',
        model: model
      });
    });
    
    // Sort models within each host group by name
    Object.keys(grouped).forEach(host => {
      grouped[host].sort((a, b) => a.label.localeCompare(b.label));
    });
    
    return grouped;
  }, [modelsData]);

  const options = [
    { id: "", label: "Default group" },
    { id: "group:standard", label: "Standard Model Group" },
    { id: "group:premium", label: "Premium Model Group" },
    { id: "group:reasoning", label: "Reasoning Model Group" },
    { id: "group:superpremium", label: "Super Premium Model Group" },
    { id: "group:supersmall", label: "Super Small Model Group" },
    { id: "openrouter:standard", label: "OpenRouter:Standard Model Group" },
    { id: "openrouter:premium", label: "OpenRouter:Premium Model Group" },

    ...(groupData?.data?.groupsByProjectId?.items?.map((group) => ({
      id: group?.id ?? "",
      label: "Custom group: " + (group?.name ?? ""),
    })) ?? []),

    // Add models grouped by host
    ...Object.keys(modelsByHost)
      .sort()
      .flatMap((host) => [
        // Add a separator/header for each host
        { id: `header:${host}`, label: `--- ${host} Models ---`, disabled: true },
        // Add all models for this host
        ...modelsByHost[host]
      ])
  ];

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading agent</div>;

  const handleUpdate = (values: AgentFormType) => {
    if (!values) return;

    try {
      const updatedAt = agentData?.data?.getLLMAgent?.updatedAt;
      mutationUpdateAgent.mutate({
        input: { id: id, ...values } as any,
        condition: updatedAt ? { updatedAt: { eq: updatedAt } } : undefined,
      });
    } catch (error) {
      logError("Error updating agent", error as any);
    }
  };

  const handleCreate = (values: AgentFormType) => {
    if (!values) return;
    if (!project) return;

    mutationCreateAgent.mutate({
      projectId: project?.id,
      ...values,
    });
  };

  const handleClose = () => {
    if (isDirty) {
      setUnsavedCloseDialogOpen(true);
      return;
    }

    navigate("/agents");
  };

  const handleConfirmClose = () => {
    setUnsavedCloseDialogOpen(false);
    navigate("/agents");
  };

  const handleCancelClose = () => {
    setUnsavedCloseDialogOpen(false);
  };

  const onSubmit = (data: AgentFormType) => {
    // Enforce new rule: auto-disable RAG/MCP if there are no resources
    const docCount =
      documentsData?.data?.agentDocumentsByAgentId?.items?.filter(
        (d: any) => d && d.status !== "deleted"
      ).length || 0;
    const mcpCount =
      agentData?.data?.getLLMAgent?.mcpServers?.items?.filter(
        (m: any) => m && m.status === "active"
      ).length || 0;

    const adjusted: AgentFormType = { ...data };
    if (docCount === 0) {
      adjusted.enableRAG = false;
    }
    if (mcpCount === 0) {
      adjusted.enableMCP = false;
    }

    if (id) handleUpdate(adjusted);
    else handleCreate(adjusted);
  };

  const themeWatch = watch("displayTheme");
  const cssUrlWatch = watch("cssUrl");
  const widthWatch = watch("displayWidth");
  const heightWatch = watch("displayHeight");
  const titleWatch = watch("displayTitle");
  const initialWatch = watch("displayInitialMessageOrPrompt");
  const startWatch = watch("displayStartMessageOrPrompt");
  const promptTemplateWatch = watch("displayPromptTemplate");
  const followOnPromptsWatch = watch("displayFollowOnPrompts");
  const actionsWatch = watch("displayActions");
  const displayPlaceholderWatch = watch("displayPlaceholder");
  const displayHideInitialPromptWatch = watch("displayHideInitialPrompt");
  const displayShowSaveButtonWatch = watch("displayShowSaveButton");
  const displayShowEmailButtonWatch = watch("displayShowEmailButton");
  const displayShowCallToActionWatch = watch("displayShowCallToAction");
  const displayCallToActionButtonTextWatch = watch(
    "displayCallToActionButtonText"
  );
  const displayCallToActionEmailAddressWatch = watch(
    "displayCallToActionEmailAddress"
  );
  const displayCallToActionEmailSubjectWatch = watch(
    "displayCallToActionEmailSubject"
  );
  const dataWatch = watch("data");
  const groupIdWatch = watch("groupId");
  const displayScrollToEndWatch = watch("displayScrollToEnd");

  const ragQueryLimitWatch = watch("ragQueryLimit");
  const ragRankLimitWatch = watch("ragRankLimit");
  const enableRAGWatch = watch("enableRAG");
  const enableMCPWatch = watch("enableMCP");

  const createConversationOnFirstChatWatch = watch(
    "createConversationOnFirstChat"
  );
  const customerEmailCaptureModeWatch = watch("customerEmailCaptureMode");
  const customerEmailCapturePlaceholderWatch = watch(
    "customerEmailCapturePlaceholder"
  );

  const getActionsArraySafely = (actionsString: string) => {
    let actions: any[] = [];
    if (actionsString && actionsString !== "") {
      try {
        actions = JSON.parse(actionsString);
        if (!Array.isArray(actions)) {
          throw new Error("Parsed actions is not an array");
        }
      } catch (error) {
        actions = [];
      }
    }
    return actions;
  };

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const createStaticHTMLPage = async () => {
    if (!id) return;

    console.log(staticHeight);

    try {
      await API.post("eventapi", "/agentStaticPage", {
        body: {
          agentId: id,
          title: titleWatch,
          width: widthWatch,
          height: heightWatch,
          htmlHeight: staticHeight,
          origin: window.location.origin,
        },
      });

      setStaticUrl("https://agent.llmasaservice.io/" + id + ".html");
    } catch (error) {
      console.error("Error creating static page:", error);
    }
  };

  const isValidUrl = (url: string) => {
    const pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])?)\\.)+[a-z]{2,}|" + // domain name
        "localhost|" + // localhost
        "\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}|" + // OR ipv4
        "\\[([0-9a-f]{1,4}:){7}[0-9a-f]{1,4}\\])" + // OR ipv6
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$",
      "i"
    ); // fragment locator
    return !!pattern.test(url);
  };

  return (
    <>
      <Paper
        sx={{
          margin: 2,
          padding: 2,
          borderRadius: 3,
          border: "1px solid #444",
        }}
      >
        {saveConflictMessage && (
          <Alert
            severity="error"
            onClose={() => setSaveConflictMessage(null)}
            sx={{ mb: 2 }}
          >
            {saveConflictMessage}
          </Alert>
        )}

        {/* Agent Version Tabs - only show for existing agents with multiple versions */}
        {id && allAgentsData?.data?.agentsByProjectId?.items && (
          <AgentVersionTabs
            currentAgentId={id}
            allAgents={allAgentsData.data.agentsByProjectId.items}
            currentAgent={agentData?.data?.getLLMAgent}
            isFormDirty={isDirty}
            getFormValues={getValues}
            onVersionChange={(newAgentId) => {
              // The navigation is handled in the component
            }}
          />
        )}

        {/* Persistent version indicator */}
        {versionLabel && (
          <Chip
            label={`Editing ${versionLabel}`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ mb: 1 }}
          />
        )}

        <Tabs
          value={selectedTab}
          onChange={(event: React.SyntheticEvent, newValue: number) =>
            setSelectedTab(newValue)
          }
        >
          <Tab label="Settings" />
          <Tab label="UI Panel" />
          <Tab label="Data" />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                Documents&nbsp;{" "}
                <Chip
                  size="small"
                  label="dev"
                  title="Developer level feature"
                  color={subscriptionActive ? "default" : "warning"}
                />
              </Box>
            }
            disabled={!id || !(trialDaysRemaining > 0 || subscriptionActive)}
          />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                MCP Servers&nbsp;{" "}
                <Chip
                  size="small"
                  label="ent"
                  title="ALPHA Enterprise level feature"
                  color={subscriptionActive ? "default" : "warning"}
                />
              </Box>
            }
            disabled={!id || !(trialDaysRemaining > 0 || subscriptionActive)}
          />
          <Tab
            label="Validation"
          />
          <Tab label="ContextOps" />

          {!isProduction && <Tab label="Style" />}
        </Tabs>

        <Grid container>
          <Grid item xs={12} md={9}>
            {selectedTab === 0 && (
              <>
                <Grid container spacing={2} mt={2}>
                  <Grid item xs={12} md={4}>
                    <TextFieldElement
                      control={control}
                      name="name"
                      label="Name"
                      size="small"
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Model/Service Group (blank for default/standard)</InputLabel>
                      <Select
                        value={watch("groupId") || ""}
                        label="Model/Service Group (blank for default/standard)"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 400,
                              minWidth: 350,
                            },
                          },
                        }}
                        onChange={(e) => {
                          // Don't allow selection of disabled header items
                          const value = e.target.value;
                          if (!value.startsWith("header:")) {
                            setValue("groupId", value);
                          }
                        }}
                      >
                        {options.map((option) => {
                          // Check if this is a model entry
                          const isModel = option.id.startsWith("model:");
                          const model = isModel ? option.model : null;
                          
                          return (
                            <MenuItem
                              key={option.id}
                              value={option.id}
                              disabled={option.disabled || false}
                              sx={option.disabled ? { fontWeight: 'bold', color: 'text.secondary' } : {}}
                            >
                              <Box display="flex" alignItems="center" gap={1} width="100%">
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                  {option.label}
                                </Typography>
                                {isModel && model && (
                                  <Box display="flex" gap={0.5} flexWrap="wrap">
                                    {model.supportsSearch && (
                                      <Chip 
                                        label="search" 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.55rem', height: 18, '& .MuiChip-label': { px: 0.5 } }}
                                      />
                                    )}
                                    {model.thinkingSupport && (
                                      <Chip 
                                        label="reasoning" 
                                        size="small" 
                                        color="secondary" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.55rem', height: 18, '& .MuiChip-label': { px: 0.5 } }}
                                      />
                                    )}
                                    {model.defaultServiceGroup && (
                                      <Chip 
                                        label={model.defaultServiceGroup?.toLowerCase()} 
                                        size="small" 
                                        color="default" 
                                        variant="filled"
                                        sx={{ fontSize: '0.55rem', height: 18, '& .MuiChip-label': { px: 0.5 } }}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <SelectElement
                      control={control}
                      name="status"
                      label="Agent status"
                      size="small"
                      required
                      fullWidth
                      options={[
                        { id: "active", label: "Active" },
                        { id: "disabled", label: "Disabled" },
                        { id: "deleted", label: "Deleted" },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <SelectElement
                      control={control}
                      name="visibility"
                      label="Agent visibility"
                      size="small"
                      fullWidth
                      options={[
                        { id: LLMVisibility.PUBLIC, label: "Public" },
                        { id: LLMVisibility.ENTERPRISE, label: "Enterprise" },
                        { id: LLMVisibility.APPLICATION, label: "Application" },
                        { id: LLMVisibility.ALL, label: "All" },
                      ]}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={2} mt={2}>
                    <SelectElement
                      control={control}
                      name="injectMemories"
                      label="Inject Customer Memories"
                      size="small"
                      fullWidth
                      error={!!errors.injectMemories}
                      helperText={errors.injectMemories?.message}
                      options={[
                        { id: 0, label: "Off" },
                        { id: 5, label: "Up to 5 memories" },
                        { id: 10, label: "Up to 10 memories" },
                        { id: 15, label: "Up to 15 memories" },
                        { id: 25, label: "Up to 25 memories" },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={2} mt={2}>
                    <SelectElement
                      control={control}
                      name="injectKnowledgeGraph"
                      label="Inject Knowledge Graph"
                      size="small"
                      fullWidth
                      error={!!errors.injectKnowledgeGraph}
                      helperText={errors.injectKnowledgeGraph?.message}
                      options={[
                        { id: 0, label: "Off" },
                        { id: 1, label: "On" },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={4} mt={2}>
                    <SelectElement
                      control={control}
                      name="thinkingDepth"
                      label="Thinking / Reasoning Depth (if model supported)"
                      size="small"
                      fullWidth
                      options={[
                        { id: LLMThinkingDepth.OFF, label: "Off" },
                        { id: LLMThinkingDepth.AUTO, label: "Auto" },
                        { id: LLMThinkingDepth.LOW, label: "Low" },
                        { id: LLMThinkingDepth.MEDIUM, label: "Medium" },
                        { id: LLMThinkingDepth.HIGH, label: "High" },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} mt={2}>
                    <CheckboxElement
                      control={control}
                      name="enableWebSearch"
                      label="Enable Web Search (if model supported)"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6} mt={2}>
                    <TextFieldElement
                      control={control}
                      name="linkedFileTypes"
                      label="Linked attachment file types"
                      size="small"
                      fullWidth
                      helperText="Comma-separated extensions (for example: pdf, png, md), image/*, or * for all supported types. Blank disables linked attachments."
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="description"
                    label="Description / Purpose"
                    size="small"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} mt={2}>
                  <TextareaAutosizeElement
                    control={control}
                    name="systemInstructions"
                    label="Agent System Instructions"
                    size="small"
                    fullWidth
                    rows={15}
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <SelectElement
                    control={control}
                    name="systemInstructionsAction"
                    label="System Instructions Action"
                    size="small"
                    required
                    fullWidth
                    options={[
                      {
                        id: "append",
                        label: "Append to project instructions",
                      },
                      {
                        id: "replace",
                        label: "Replace project instructions",
                      },
                    ]}
                  />
                </Grid>
              </>
            )}

            {selectedTab === 1 && (
              <>
                <Grid container mt={2} spacing={2}>
                  <Grid item xs={4}>
                    <TextFieldElement
                      control={control}
                      name="displayTitle"
                      label="What title do you want on the panel?"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <SelectElement
                      control={control}
                      name="displayTheme"
                      label="Dark or light panel theme?"
                      size="small"
                      fullWidth
                      options={[
                        { id: "dark", label: "Dark theme" },
                        { id: "light", label: "Light theme" },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextFieldElement
                      control={control}
                      name="cssUrl"
                      label="URL to CSS file (leave blank for default)"
                      size="small"
                      fullWidth
                    />
                    <Link
                      href="https://help.llmasaservice.io/docs/developers/customthemes"
                      target="_blank"
                    >
                      <Typography variant="caption">
                        Learn more about custom CSS themes
                      </Typography>
                    </Link>
                  </Grid>
                </Grid>
                <Grid container mt={2} spacing={2}>
                  <Grid item xs={3}>
                    <TextFieldElement
                      control={control}
                      name="displayWidth"
                      label="Width (use px or %, e.g. 100vw)"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextFieldElement
                      control={control}
                      name="displayHeight"
                      label="Height (use px or %, e.g. 100vh)"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextFieldElement
                      control={control}
                      name="displayPlaceholder"
                      label="Prompt placeholder text"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2} spacing={2}>
                  <Grid item xs={6}>
                    <TextFieldElement
                      control={control}
                      name="displayInitialMessageOrPrompt"
                      label="Initial Agent Message or Prompt to show"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <SelectElement
                      control={control}
                      name="displayStartMessageOrPrompt"
                      label="Show this as a message or use as an initial prompt?"
                      size="small"
                      fullWidth
                      options={[
                        { id: "message", label: "Message" },
                        { id: "prompt", label: "Prompt" },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <CheckboxElement
                      control={control}
                      name="displayHideInitialPrompt"
                      label="Hide prompt (just show response)"
                      size="small"
                    />
                  </Grid>
                </Grid>
                <Grid container mt={1} spacing={2}>
                  <Grid item xs={3}>
                    <CheckboxElement
                      control={control}
                      name="createConversationOnFirstChat"
                      label="Create conversations"
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <SelectElement
                      control={control}
                      name="customerEmailCaptureMode"
                      label="Email capture mode"
                      size="small"
                      fullWidth
                      options={[
                        {
                          id: LLMEmailCaptureMode.HIDE,
                          label: "Hide (unless needed)",
                        },
                        {
                          id: LLMEmailCaptureMode.OPTIONAL,
                          label: "Optional (shown, but optional)",
                        },
                        {
                          id: LLMEmailCaptureMode.REQUIRED,
                          label: "Require valid email to chat",
                        },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={5}>
                    <TextFieldElement
                      control={control}
                      name="customerEmailCapturePlaceholder"
                      label="Email capture placeholder text"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                </Grid>

                {/*
                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="displayPromptTemplate"
                    label="First User Prompt Template (remember to include {{prompt}} where you want the users prompt to be inserted)"
                    size="small"
                    fullWidth
                  />
                </Grid>
                */}
                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="displayFollowOnPrompts"
                    label="Follow on prompts (buttons shown after a response to keep the conversation going, add multiple separated by |)"
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="displayActions"
                    label="Inline Response Call to Actions (Advanced: see documentation for details)"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <CheckboxElement
                    control={control}
                    name="displayShowSaveButton"
                    label="Show save button"
                    size="small"
                  />
                  <CheckboxElement
                    control={control}
                    name="displayShowEmailButton"
                    label="Show email button"
                    size="small"
                  />
                  <CheckboxElement
                    control={control}
                    name="displayShowCallToAction"
                    label="Show Call to Action button"
                    size="small"
                  />
                  <CheckboxElement
                    control={control}
                    name="displayScrollToEnd"
                    label="Autoscroll responses"
                    size="small"
                  />
                </Grid>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={3}>
                    <TextFieldElement
                      control={control}
                      name="displayCallToActionButtonText"
                      label="Call to Action button text"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldElement
                      control={control}
                      name="displayCallToActionEmailAddress"
                      label="Call to Action email address"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextFieldElement
                      control={control}
                      name="displayCallToActionEmailSubject"
                      label="Call to Action email subject"
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <hr />

                    <Typography variant="caption">
                      For embedding in some websites a static page is required
                      (eg. IFramely compatible sites like Canva). Clicking this
                      button creates a static page for that purpose.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      onClick={() => createStaticHTMLPage()}
                      variant="outlined"
                      size="small"
                    >
                      Create static page link
                    </Button>
                    &nbsp;&nbsp;
                    <TextField
                      label="Height of static page (px)"
                      sx={{ width: 175 }}
                      size="small"
                      value={staticHeight}
                      onChange={(e) => setStaticHeight(e.target.value)}
                    />
                    {staticUrl && (
                      <>
                        &nbsp;&nbsp;
                        <Typography variant="caption">{staticUrl}</Typography>
                        <IconButton
                          onClick={() => {
                            navigator.clipboard.writeText(staticUrl);
                          }}
                          size="small"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </>
                    )}
                    <hr />
                  </Grid>
                </Grid>
              </>
            )}

            {selectedTab === 2 && (
              <>
                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="data"
                    label="Data"
                    size="small"
                    fullWidth
                    multiline
                    rows={25}
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <Button
                    onClick={() =>
                      navigator.clipboard.writeText(
                        `fetch(
"https://api.llmasaservice.io/agents/${id}",
{
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${apiKey1}" // api key 1 from the Integration API page if available
  },
  body: JSON.stringify({
    action: "data",
    data: "new data here" // add your new data string here
  })
}
)
.then((response) => response.text())
.then((result) => console.log(result))
.catch((error) => console.error("Error:", error));`
                      )
                    }
                    variant="text"
                  >
                    Copy JS Fetch Update code
                  </Button>
                </Grid>
              </>
            )}

            {selectedTab === 3 && (
              <>
                <Grid item xs={12} mt={2}>
                  <CheckboxElement
                    control={control}
                    name="enableRAG"
                    label="Enable Retrieval Augmented Generation (RAG)"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} mt={1}>
                  <Typography variant="h6" mb={2}>
                    Retrieval Settings
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <TextFieldElement
                        control={control}
                        type="number"
                        name="ragQueryLimit"
                        label="Query Limit"
                        size="small"
                        fullWidth
                        error={!!errors.ragQueryLimit}
                        helperText={errors.ragQueryLimit?.message}
                        disabled={!enableRAGWatch}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextFieldElement
                        control={control}
                        type="number"
                        name="ragRankLimit"
                        label="Rank Limit"
                        size="small"
                        fullWidth
                        error={!!errors.ragRankLimit}
                        helperText={errors.ragRankLimit?.message}
                        disabled={!enableRAGWatch}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <CheckboxElement
                        control={control}
                        name="ragIncludeCitations"
                        label="Include citations"
                        size="small"
                        disabled={!enableRAGWatch}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <CheckboxElement
                        control={control}
                        name="ragSkipChecksOnContext"
                        label="Skip checks"
                        size="small"
                        disabled={!enableRAGWatch}
                      />
                    </Grid>
                    {showDevRagControls && (
                      <Grid item xs={3}>
                        <CheckboxElement
                          control={control}
                          name="ragShadowRead"
                          label="OpenAI shadow read"
                          size="small"
                          disabled={!enableRAGWatch}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ padding: 2 }}>
                        <Button
                          onClick={handleSubmit(onSubmit)}
                          variant={"contained"}
                          sx={{ marginRight: 2 }}
                        >
                          {versionLabel ? `Save ${versionLabel} and Close` : 'Save and Close'}
                        </Button>
                        <Button onClick={() => reset()} variant={"outlined"}>
                          Reset
                        </Button>
                        <Button onClick={handleClose} variant={"outlined"}>
                          Cancel
                        </Button>
                      </Box>
                    </Grid>{" "}
                  </Grid>

                  <hr />
                  <Typography variant="h6">Indexed Documents</Typography>
                  <List sx={{ opacity: enableRAGWatch ? 1 : 0.5, pointerEvents: enableRAGWatch ? 'auto' : 'none' }}>
                    {openAIIndexedDocuments.map(
                      (doc) => (
                        <ListItem key={doc?.id}>
                          <ListItemText
                            primary={doc?.fileName ?? doc?.location}
                            secondary={
                              doc?.fileType
                                ? `${doc?.status} - ${doc?.fileType}, ${
                                    doc?.fileSize
                                  } bytes (splitter: ${
                                    doc?.ragSplitter || "default"
                                  }, chunk size: ${
                                    doc?.ragChunkSize || 1000
                                  }, overlap: ${
                                    doc?.ragChunkOverlap ?? "unknown"
                                  })`
                                : `${doc?.status}`
                            }
                          />
                          <Box display="flex" justifyContent="flex-end">
                            {!deleting || confirmDelete !== doc?.id ? (
                              <ListItemButton
                                onClick={async () => {
                                  if (confirmDelete === doc?.id) {
                                    setDeleting(true);
                                    await API.post(
                                      "eventapi",
                                      "/deleteDocumentFromAgent",
                                      {
                                        body: {
                                          projectId: doc?.projectId,
                                          agentId: doc?.agentId,
                                          agentDocumentId: doc?.id,
                                        },
                                      }
                                    );

                                    queryClient.invalidateQueries({
                                      queryKey: [
                                        "LLMAgentDocuments",
                                        { agentId: id },
                                      ],
                                    });
                                    setDeleting(false);
                                    setConfirmDelete("");
                                  } else {
                                    setConfirmDelete(doc?.id ?? "");
                                  }
                                }}
                              >
                                {confirmDelete === (doc?.id ?? "")
                                  ? "Confirm Delete"
                                  : "Delete"}
                              </ListItemButton>
                            ) : (
                              <Typography variant="body2">
                                Deleting...
                              </Typography>
                            )}
                          </Box>
                        </ListItem>
                      )
                    )}
                  </List>

                  <hr />
                  <Typography variant="h6" mb={2}>
                    Add/Replace Documents
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sx={{ opacity: enableRAGWatch ? 1 : 0.5, pointerEvents: enableRAGWatch ? 'auto' : 'none' }}>
                      <FileUploader
                        agentId={id ?? ""}
                        projectId={
                          agentData?.data?.getLLMAgent?.projectId ??
                          project?.id ??
                          ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12}></Grid>
                    <Grid item xs={12} sx={{ opacity: enableRAGWatch ? 1 : 0.5, pointerEvents: enableRAGWatch ? 'auto' : 'none' }}>
                      <UrlScrape
                        agentId={id ?? ""}
                        projectId={
                          agentData?.data?.getLLMAgent?.projectId ??
                          project?.id ??
                          ""
                        }
                        documents={
                          documentsData as
                            | GraphQLResult<AgentDocumentsByAgentIdQuery>
                            | undefined
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} mt={2}>
                    <Typography variant="caption">
                      Or, Upload new or replace existing document by Public API
                    </Typography>
                    <br />
                    <Button
                      onClick={() =>
                        navigator.clipboard.writeText(
                          `fetch("https://api.llmasaservice.io/agents/${id}", {
  method: 'POST',
  headers:  {
        "Content-Type": "application/json",
        "Authorization": "Bearer ${apiKey1}" // key 1 from the Integration API page if available
    },
  body: JSON.stringify({
     "action": "document",
     "location": "[http(s) url to your document]", // this needs to be a publicly accessible URL that a plain HTTP GET will return
     "ragSplitter": "default", // use values "default", "newline" (best for CSV files), "blankline" (best for files where there is a blankline between sections)
     "ragChunkSize": 1000, // the number of characters MAXIMUM in each chunk. The default is 1000, but you can increase this to 5000 or even 10000 if you want to get more context in each chunk. This is the number of characters in the text, not the number of tokens.
     "ragChunkOverlap": 10 // the number of characters to overlap between chunks. The default is 10, but you can increase this to 100 or even 1000 if you want to get more context in each chunk. This is the number of characters in the text, not the number of tokens.
})
                      })
  .then(response => response.text())
  .then(result => console.log(result))
  .catch(error => console.log('error', error));`
                        )
                      }
                      variant="text"
                      disabled={!enableRAGWatch}
                    >
                      Copy JS Fetch Upload API code
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}

            {selectedTab === 4 && (
              <>
                <Grid item xs={12} mt={2}>
                  <CheckboxElement
                    control={control}
                    name="enableMCP"
                    label="Enable MCP Servers"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} mt={1}>
                  <Typography variant="h6" mb={2}>
                    MCP Server Settings
                  </Typography>
                  <List sx={{ opacity: enableMCPWatch ? 1 : 0.5, pointerEvents: enableMCPWatch ? 'auto' : 'none' }}>
                    {agentData?.data?.getLLMAgent?.mcpServers?.items
                      .filter(
                        (client: any) =>
                          showDeletedMCPs || client?.status === "active"
                      )
                      .map((client: any) => (
                        <ListItem key={client?.id}>
                          <ListItemText
                            primary={
                              client?.status !== "active" ? (
                                <span
                                  style={{ textDecoration: "line-through" }}
                                >
                                  {client?.url}
                                </span>
                              ) : (
                                client?.url
                              )
                            }
                            secondary={`Executed on the: ${
                              client?.executionMode || "CLIENT"
                            }`}
                          />
                          <Button
                            variant="text"
                            color={
                              client?.status === "active" ? "error" : "primary"
                            }
                            size="small"
                            onClick={async () => {
                              const action =
                                client?.status === "active"
                                  ? "delete"
                                  : "restore";
                              if (
                                window.confirm(
                                  `Are you sure you want to ${action} this MCP server: ${client?.sseUrl}?`
                                )
                              ) {
                                try {
                                  await API.graphql(
                                    graphqlOperation(updateLLMAgentMCP, {
                                      input: {
                                        id: client?.id,
                                        status:
                                          client?.status === "active"
                                            ? "deleted"
                                            : "active",
                                      },
                                    })
                                  );

                                  // Refresh the data
                                  queryClient.invalidateQueries({
                                    queryKey: ["agents", { id: id }],
                                  });
                                } catch (error) {
                                  logError(
                                    `Error ${action}ing MCP client`,
                                    error as any
                                  );
                                }
                              }
                            }}
                          >
                            {client?.status === "active" ? "Delete" : "Restore"}
                          </Button>
                        </ListItem>
                      ))}
                  </List>
                  <Box display="flex" alignItems="center" mt={1} sx={{ opacity: enableMCPWatch ? 1 : 0.5, pointerEvents: enableMCPWatch ? 'auto' : 'none' }}>
                    <Button
                      onClick={() => setOpenMCPDialog(true)}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 2 }}
                    >
                      Add MCP Server
                    </Button>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showDeletedMCPs}
                          onChange={(e) => setShowDeletedMCPs(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Show Deleted"
                    />
                  </Box>
                  <Box sx={{ padding: 2 }}>
                    <Button
                      onClick={handleSubmit(onSubmit)}
                      variant={"contained"}
                      sx={{ marginRight: 2 }}
                    >
                      {versionLabel ? `Save ${versionLabel} and Close` : 'Save and Close'}
                    </Button>
                    <Button onClick={() => reset()} variant={"outlined"}>
                      Reset
                    </Button>
                    <Button onClick={handleClose} variant={"outlined"}>
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </>
            )}

            <Dialog
              open={openMCPDialog}
              onClose={() => {
                setOpenMCPDialog(false);
                setMcpUrl("");
                setMcpApiKey("");
                setShowMcpApiKey(false);
                setExecutionMode("CLIENT");
              }}
            >
              <DialogTitle>Add MCP Server</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Enter the URL of the MCP server to add to this agent.
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  id="mcpUrl"
                  label="MCP Server URL"
                  type="text"
                  fullWidth
                  variant="standard"
                  value={mcpUrl}
                  onChange={(e) => setMcpUrl(e.target.value)}
                  error={mcpUrlError}
                  helperText={mcpUrlError ? "Invalid URL" : ""}
                />
                <Box sx={{ position: "relative", mt: 1 }}>
                  <TextField
                    margin="dense"
                    id="mcpApiKey"
                    label="API Key (optional)"
                    type={showMcpApiKey ? "text" : "password"}
                    fullWidth
                    variant="standard"
                    value={mcpApiKey}
                    onChange={(e) => setMcpApiKey(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowMcpApiKey(!showMcpApiKey)}
                          edge="end"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          {showMcpApiKey ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
                <FormControl fullWidth margin="dense" variant="standard">
                  <InputLabel id="execution-mode-label">
                    Execution Mode
                  </InputLabel>
                  <Select
                    labelId="execution-mode-label"
                    id="executionMode"
                    value={executionMode}
                    onChange={(e) => setExecutionMode(e.target.value)}
                    label="Execution Mode"
                  >
                    <MenuItem value="CLIENT">CLIENT</MenuItem>
                    <MenuItem value="SERVER">SERVER</MenuItem>
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setOpenMCPDialog(false);
                    setMcpUrl("");
                    setMcpApiKey("");
                    setShowMcpApiKey(false);
                    setExecutionMode("CLIENT");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!id) return;

                    if (!isValidUrl(mcpUrl)) {
                      setMcpUrlError(true);
                      return;
                    }

                    await mcpCreateMutation.mutateAsync({
                      mcpUrl,
                      executionMode,
                      accessToken: mcpApiKey || undefined,
                    });
                  }}
                >
                  Add
                </Button>
              </DialogActions>
            </Dialog>

            {selectedTab === 5 && id && (
              <ValidationTab agentId={id} projectId={project?.id || ""} />
            )}

            {selectedTab === 6 && (
              <>
                <Typography variant="h6" mt={2} mb={1}>
                  ContextOps — Cache, Compaction &amp; Curation
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Optimize token usage and costs across multi-turn conversations.
                </Typography>

                <Paper sx={{ p: 2, mb: 2, border: "1px solid #444", borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Prompt Caching</Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Cache static portions of prompts (system instructions, tools, conversation history) to reduce input token costs. Supported on Anthropic, OpenAI, and Google models.
                  </Typography>
                  <CheckboxElement
                    control={control}
                    name="enablePromptCaching"
                    label="Enable prompt caching"
                  />
                </Paper>

                <Paper sx={{ p: 2, mb: 2, border: "1px solid #444", borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Compaction</Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Automatically summarize conversation history when it exceeds a token threshold, preventing context window overflow. Supported on Anthropic and OpenAI Responses API models.
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <SelectElement
                        control={control}
                        name="compactionPolicy"
                        label="Compaction Policy"
                        size="small"
                        fullWidth
                        options={[
                          { id: "OFF", label: "Off — no compaction" },
                          { id: "AUTO", label: "Auto — default threshold" },
                          { id: "CUSTOM", label: "Custom threshold" },
                        ]}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextFieldElement
                        control={control}
                        name="compactionThreshold"
                        label="Compaction threshold (tokens)"
                        size="small"
                        type="number"
                        fullWidth
                        disabled={watch("compactionPolicy") !== "CUSTOM"}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper sx={{ p: 2, mb: 2, border: "1px solid #444", borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Context Curation</Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Remove old tool results from conversation history to reduce token usage without losing conversational context.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <CheckboxElement
                        control={control}
                        name="curateToolResults"
                        label="Curate old tool results (replace with placeholder after N turns)"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextFieldElement
                        control={control}
                        name="curateAfterTurns"
                        label="Curate after N turns"
                        size="small"
                        type="number"
                        fullWidth
                        disabled={!watch("curateToolResults")}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </>
            )}

            {selectedTab === 7 && (
              <>
                <Grid item xs={12} mt={2}>
                  <TextField
                    label="Style Text"
                    size="small"
                    fullWidth
                    multiline
                    rows={25}
                    onChange={(e) => setStyleText(e.target.value)}
                  />
                </Grid>
              </>
            )}

            {(selectedTab < 3 || selectedTab === 6) && (
              <Box sx={{ padding: 2 }}>
                <Button
                  onClick={handleSubmit(onSubmit)}
                  variant={"contained"}
                  sx={{ marginRight: 2 }}
                >
                  {versionLabel ? `Save ${versionLabel} and Close` : 'Save and Close'}
                </Button>
                <Button onClick={() => reset()} variant={"outlined"}>
                  Reset
                </Button>
                <Button onClick={handleClose} variant={"outlined"}>
                  Cancel
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper
              sx={{ margin: 2, borderRadius: 3, border: "1px solid #444" }}
            >
              <Typography padding={2} variant="h6">
                Agent Preview
              </Typography>
              {agentData?.data?.getLLMAgent && (
                <ChatPanel
                  project_id={project?.id || ""}
                  service={groupIdWatch ?? null}
                  url={url}
                  title={titleWatch ?? "Chat Agent"}
                  theme={themeWatch === "light" ? "light" : "dark"}
                  height={heightWatch ?? "75vh"}
                  width={widthWatch ?? "100%"}
                  cssUrl={styleText === "" ? cssUrlWatch ?? "" : styleText}
                  promptTemplate={promptTemplateWatch ?? "{{prompt}}"}
                  initialMessage={
                    startWatch === "message" ? initialWatch ?? "" : undefined
                  }
                  initialPrompt={
                    startWatch === "prompt" ? initialWatch ?? "" : undefined
                  }
                  followOnQuestions={followOnPromptsWatch?.split("|") ?? []}
                  actions={getActionsArraySafely(actionsWatch ?? "")}
                  agent={agentData?.data?.getLLMAgent?.id ?? null}
                  placeholder={displayPlaceholderWatch ?? "Type a message"}
                  hideInitialPrompt={displayHideInitialPromptWatch ?? true}
                  data={dataWatch ? [{ key: "data", data: dataWatch }] : []}
                  showEmailButton={displayShowEmailButtonWatch ?? true}
                  showSaveButton={displayShowSaveButtonWatch ?? true}
                  showCallToAction={displayShowCallToActionWatch ?? false}
                  callToActionButtonText={
                    displayCallToActionButtonTextWatch ?? "Submit"
                  }
                  callToActionEmailAddress={
                    displayCallToActionEmailAddressWatch ?? ""
                  }
                  callToActionEmailSubject={
                    displayCallToActionEmailSubjectWatch ??
                    "Agent CTA Submitted"
                  }
                  scrollToEnd={displayScrollToEndWatch ?? false}
                  //- clearFollowOnQuestionsNextPrompt = false,
                  //- thumbsUpClick,
                  //- thumbsDownClick,
                  //- markdownClass = null,
                  //- historyChangedCallback = null,
                  //- followOnPrompt = "",
                  //- messages = [],
                  createConversationOnFirstChat={
                    createConversationOnFirstChatWatch ?? true
                  }
                  customerEmailCaptureMode={
                    customerEmailCaptureModeWatch ?? LLMEmailCaptureMode.HIDE
                  }
                  customerEmailCapturePlaceholder={
                    customerEmailCapturePlaceholderWatch ??
                    "Please enter your email..."
                  }
                  //mcpServers={}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={unsavedCloseDialogOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You have unsaved changes. Leaving this page will discard them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color="inherit">
            Stay on This Page
          </Button>
          <Button onClick={handleConfirmClose} variant="contained" color="warning">
            Discard and Leave
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentForm;
